import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import StandardModal from "./shared/StandardModal";
import { MoveFocusInside } from "react-focus-lock";
import ToggleSwitch from "js/components/shared/ToggleSwitch";
import { InfoBox } from "./shared/InfoBox";
import { PrimaryButton } from "./shared/button/PrimaryButton";
import { makeYoutubeEmbedUrl, isYoutubeVideoUrl } from "js/utils/youtubeUrlUtils";
import * as clsApi from "../api/classroomApi";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { WarningBanner } from "@/components/WarningBanner";

interface User {
	username: string;
}

interface SendNewTabModalProps {
	users?: User[];
	open: boolean;
	title: string;
	onClose: () => void;
	classroomSourceId: string;
}

interface OpenTabPayload {
	classroom_source_id: string;
	event_object: {
		url: string;
	};
	recipients?: string[];
	classroom_id?: string | null;
}

export const SendNewTabModal: React.FC<SendNewTabModalProps> = ({ users, open, title, onClose, classroomSourceId }) => {
	const { t } = useTranslation();
	const [currentUrl, setCurrentUrl] = useState("");
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | false>(false);
	const [youtubeFullscreen, setYoutubeFullscreen] = useState(false);

	const tabLimit = useClassroomPolicyStore((state) => state.getTabLimit());

	const close = useCallback(() => {
		setSending(false);
		setCurrentUrl("");
		onClose();
	}, [onClose]);

	const sendMessage = useCallback(() => {
		if (!currentUrl) {
			setError(t("sendNewTabModal.errorEnterWebsite"));
			return;
		}

		let finalUrl = currentUrl;

		if (youtubeFullscreen) {
			finalUrl = makeYoutubeEmbedUrl(currentUrl);
		}

		setSending(true);
		setError(false);

		const payload: OpenTabPayload = {
			classroom_source_id: classroomSourceId,
			event_object: {
				url: finalUrl,
			},
		};

		if (users) {
			payload.recipients = users.map((user) => user.username);
		}

		payload.classroom_id = useSelectedClassroomStore.getState().stableIdV1;

		clsApi.Post(
			"events/open-tab",
			payload,
			() => {
				close();
			},
			() => {
				setSending(false);
				setError(t("sendNewTabModal.errorOpenTabsFailed"));
			}
		);
	}, [currentUrl, youtubeFullscreen, classroomSourceId, users, close, t]);

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentUrl(event.target.value);
	}, []);

	const handleInputEnter = useCallback(
		(ev: React.KeyboardEvent<HTMLInputElement>) => {
			if (ev.key === "Enter") {
				ev.stopPropagation();
				sendMessage();
			}
		},
		[sendMessage]
	);

	const isYoutubeVideoUrlChecks = useCallback(
		(url: string): boolean => {
			if (!open) return false;
			return isYoutubeVideoUrl(url);
		},
		[open]
	);

	const handleToggleSwitch = useCallback(() => {
		setYoutubeFullscreen((prev) => !prev);
	}, []);

	const renderButtons = () => {
		return (
			<React.Fragment>
				{!sending ? (
					<PrimaryButton
						data-cy="sendNewTabModal_openButton"
						className="mui-flat-button"
						label={t("classTools.openTab")}
						onClick={sendMessage}
					/>
				) : (
					<PrimaryButton className="mui-flat-button" label={t("sendNewTabModal.sending")} />
				)}
			</React.Fragment>
		);
	};

	return (
		<StandardModal size="medium" title={title} open={open} close={close} actions={renderButtons()}>
			<form data-cy="sendNewTabModal_form" onSubmit={(ev) => ev.preventDefault()}>
				{tabLimit !== null && <WarningBanner message={t("sendNewTabModal.tabLimitWarning", { tabLimit })} />}
				<label className="block-label">{t("sendNewTabModal.websiteUrlLabel")}</label>
				<MoveFocusInside>
					<input
						className="form-input"
						onChange={handleChange}
						onKeyDown={handleInputEnter}
						value={currentUrl}
						placeholder={t("sendNewTabModal.urlPlaceholder")}
						spellCheck="false"
						type="text"
					/>
				</MoveFocusInside>
				<br />
				{isYoutubeVideoUrlChecks(currentUrl) && (
					<React.Fragment>
						<div style={{ display: "flex" }}>
							<ToggleSwitch checked={youtubeFullscreen} onToggle={handleToggleSwitch} />
							&nbsp; {t("sendNewTabModal.youtubeFullscreenToggle")}
						</div>
						<InfoBox canClose={true}>{t("sendNewTabModal.youtubeFullscreenInfo")}</InfoBox>
					</React.Fragment>
				)}
				{error && <div className={"error"}>{error}</div>}
			</form>
		</StandardModal>
	);
};
